import React, { useState, useEffect, useContext } from "react";
import { FolderIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import Preloader from "./LoadingAnimations/Preloader";
import { getFileIcon, formatFileSize } from "src/helpers/documentHelper";
import Breadcrumb from "./Settings/Breadcrumbs";
import { ArrowUpTrayIcon, HomeIcon, PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import DocumentPreviewModal from "../embedTypes/basic/Document/DocumentPreviewModal";
import { useParams } from "react-router-dom";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";
import { apiRequest } from "src/async/apiUtils";
import PaginationFooter from "../Table/PaginationFooter";
import { toast } from "react-toastify";
import { formatDate } from "src/helpers/formatDate";
import Modal from "./Modal";
import { objectToFormData } from "src/helpers";
import Search from "../form/Search";
import Checkbox from "../form/Checkbox";
import StorageUploader from "src/pages/StorageUploader";
import StorageUploadProgress from "src/pages/StorageUploadProgress";
import InputGroup from "../form/Input";
import { AuthContext } from "src/context/auth";

const UploadedDocumentList = ({ content }) => {
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [meta, setMeta] = useState({});

  const [keyword, setKeyword] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [parentId, setParentId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const [selectedDocIndex, setSelectedDocIndex] = useState(-1);
  const [pathStack, setPathStack] = useState([{ id: null, icon: HomeIcon, label: "Root" }]);

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, docId: null, isBulk: false });
  const [renameModal, setRenameModal] = useState({ isOpen: false, doc: null });
  const [newName, setNewName] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [uploadType, setUploadType] = useState("file");
  const [isUploading, setIsUploading] = useState(false);
  const [documentStatusList, setDocumentStatusList] = useState([]);
  const [uploadFormData, setUploadFormData] = useState({ name: "", documents: [] });

  const { workspace_id, content_type, content_value } = useParams();
  const { group, page, form, document } = useUrlQueryParams();

  const { user } = useContext(AuthContext);

  const commonQueries = {
    group,
    page,
    form,
    document,
    ...(content_type && content_value ? { [content_type]: content_value } : {}),
  };

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(keyword), 500);
    return () => clearTimeout(handler);
  }, [keyword]);

  const getUploadedDocuments = async () => {
    if (!user?._id) return;
    try {
      const queries = { parent_id: parentId, keyword: debouncedSearch, ...commonQueries };
      setDocuments([]);
      setLoadingDocuments(true);
      const { data } = await apiRequest("get", "portal/workspaces/:workspace_id/documents", { params: { workspace_id }, queries });
      if (data.status === 200) {
        setDocuments(data?.data);
        setMeta(data?.meta || {});
      } else {
        setDocuments([]);
        setMeta(data?.meta || {});
      }
      setSelectedIds([]);
      setLoadingDocuments(false);
    } catch (error) {
      if (!debouncedSearch) setLoadingDocuments(false);
    }
  };

  useEffect(() => {
    getUploadedDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId, group, page, form, document, workspace_id, limit, offset, debouncedSearch]);

  const confirmDelete = async () => {
    setIsProcessing(true);
    try {
      if (deleteModal?.isBulk) {
        await apiRequest("delete", `portal/workspaces/:workspace_id/documents`, {
          queries: { ...commonQueries, ids: selectedIds },
          params: { workspace_id },
        });
      } else {
        await apiRequest("delete", `portal/workspaces/:workspace_id/documents/:document_id`, {
          queries: { ...commonQueries },
          params: { document_id: deleteModal.docId, workspace_id },
        });
      }
      getUploadedDocuments();
      setDeleteModal({ isOpen: false, docId: null, isBulk: false });
    } catch (error) {
      toast.error(error?.message || "Delete failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmRename = async () => {
    if (!newName || newName === renameModal.doc.name) {
      setRenameModal({ isOpen: false, doc: null });
      return;
    }
    setIsProcessing(true);
    try {
      await apiRequest(
        "put",
        `portal/workspaces/:workspace_id/documents/:document_id`,
        {
          body: objectToFormData({ name: newName }),
          queries: { ...commonQueries },
          params: { document_id: renameModal.doc?._id, workspace_id },
        },
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      getUploadedDocuments();
      setRenameModal({ isOpen: false, doc: null });
    } catch (error) {
      toast.error(error?.message || "Rename failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === documents.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(documents.map((d) => d._id));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const handlePaginationChange = ({ limit, offset }) => {
    setLimit(limit);
    setOffset(offset);
  };

  const handleFolderClick = (doc) => {
    setPathStack((prev) => [...prev, { id: doc._id, icon: FolderIcon, label: doc.name }]);
    setParentId(doc._id);
    setOffset(0);
  };

  const handleBreadcrumbClick = (id, index) => {
    setPathStack((prev) => prev.slice(0, index + 1));
    setParentId(id);
    setOffset(0);
  };

  const handleOpen = (doc) => {
    setSelectedDocIndex(documents?.findIndex((d) => d._id === doc._id));
  };

  const handleOpenById = (id) => {
    const doc = documents.find((d) => d._id === id);
    if (doc) handleOpen(doc);
  };

  useEffect(() => {
    if (parentId === null) {
      setPathStack([{ id: null, icon: HomeIcon, label: "Root" }]);
    }
  }, [parentId]);

  const handleTabChange = (type) => {
    setUploadType(type);
    setUploadFormData({ name: "", documents: [] });
    setDocumentStatusList([]);
    setIsUploading(false);
  };

  const updateStatus = (id, status) => {
    setDocumentStatusList((prev) => prev.map((item) => (item.id === id ? { ...item, status } : item)));
  };

  const getOrCreateFolder = async (path, baseParentId, folderMap) => {
    const segments = path.split("/");
    let currentParentId = baseParentId;
    let currentPath = "";

    const params = { workspace_id };
    const queries = { group, page, form, document, ...(content_type && content_value ? { [content_type]: content_value } : {}) };

    for (const segment of segments) {
      currentPath = currentPath ? `${currentPath}/${segment}` : segment;
      const displayName = uploadFormData.folderNames?.[currentPath] || segment;

      if (folderMap[currentPath]) {
        currentParentId = folderMap[currentPath];
      } else {
        const res = await apiRequest(
          "post",
          `portal/workspaces/:workspace_id/documents`,
          {
            body: objectToFormData({ name: displayName, type: "folder", parent_id: currentParentId }),
            queries,
            params,
          },
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        if (res.status === 200) {
          const newFolderId = res?.data?.data?._id;
          folderMap[currentPath] = newFolderId;
          currentParentId = newFolderId;
        }
      }
    }
    return currentParentId;
  };

  const handleUpload = async () => {
    const docsToUpload = uploadFormData.documents;
    const folderMap = {};
    let baseParentId = parentId || null;
    const uniqueId = Math.random().toString(10).substring(2, 7) + Math.random().toString(10).substring(2, 8);

    const params = { workspace_id };
    const queries = { group, page, form, document, ...(content_type && content_value ? { [content_type]: content_value } : {}) };

    setIsUploading(true);

    if (uploadType === "new_folder") {
      if (!uploadFormData.name) {
        setIsUploading(false);
        return toast.error("Please provide a folder name");
      }
      try {
        const res = await apiRequest(
          "post",
          `portal/workspaces/:workspace_id/documents`,
          {
            body: objectToFormData({ name: uploadFormData.name, type: "folder", parent_id: parentId || null }),
            queries,
            params,
          },
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        if (res.status === 200) {
          baseParentId = res.data?.data?._id;
          if (docsToUpload.length === 0) {
            finalizeUpload();
            return;
          }
        }
      } catch (error) {
        setIsUploading(false);
        return toast.error(`Failed to create folder: ${error.message}`);
      }
    }

    if (uploadType !== "new_folder" && docsToUpload.length === 0) {
      setIsUploading(false);
      return toast.error("Please select at least one file to upload");
    }

    setDocumentStatusList(
      docsToUpload.map((d) => ({
        id: d.id,
        name: d.path || d.name,
        status: "pending",
        extension: d.file.name.split(".").pop(),
      })),
    );

    for (let i = 0; i < docsToUpload.length; i++) {
      const doc = docsToUpload[i];
      try {
        updateStatus(doc.id, "validating");

        const isLast = i === docsToUpload.length - 1;

        const validationFD = new FormData();
        validationFD.append("name", doc.name);
        validationFD.append(`documents[0][file]`, doc.file);
        validationFD.append(`documents[0][id]`, doc.id);
        validationFD.append(`documents[0][name]`, doc.name);
        validationFD.append(`documents[0][path]`, doc.path || "");
        validationFD.append("upload_batch_id", uniqueId);
        if (isLast) {
          validationFD.append("last_upload", true);
        }

        await apiRequest("post", `portal/workspaces/:workspace_id/documents/validate`, { body: validationFD, queries, params }, { headers: { "Content-Type": "multipart/form-data" } });

        let targetFolderId = baseParentId;
        const fullPath = doc.file.webkitRelativePath || doc.path || "";
        if (fullPath) {
          const pathSegments = fullPath.split("/");
          pathSegments.pop();
          if (pathSegments.length > 0) {
            targetFolderId = await getOrCreateFolder(pathSegments.join("/"), baseParentId, folderMap);
          }
        }

        updateStatus(doc.id, "uploading");

        const uploadFD = new FormData();
        uploadFD.append("name", doc.name);
        uploadFD.append("document", doc.file);
        uploadFD.append("type", "file");
        if (targetFolderId) uploadFD.append("parent_id", targetFolderId);
        uploadFD.append("upload_batch_id", uniqueId);
        if (isLast) {
          uploadFD.append("last_upload", true);
        }

        await apiRequest("post", `portal/workspaces/:workspace_id/documents`, { body: uploadFD, queries, params }, { headers: { "Content-Type": "multipart/form-data" } });

        updateStatus(doc.id, "success");
      } catch (error) {
        updateStatus(doc.id, "failed");
        if (error.message?.includes("Failed to create folder")) break;
      }
    }

    finalizeUpload();
  };
  const finalizeUpload = () => {
    setIsUploading(false);
    setUploadFormData({ name: "", documents: [] });
    setDocumentStatusList([]);
    getUploadedDocuments();
  };

  const uploadTabs = [
    { key: "file", label: "Upload Files", icon: <ArrowUpTrayIcon className="h-4 w-4" /> },
    { key: "folder", label: "Upload Folder", icon: <FolderIcon className="h-4 w-4" /> },
    { key: "new_folder", label: "New Folder", icon: <PlusIcon className="h-4 w-4" /> },
  ];

  return (
    <div className="animate-fade-in">
      {!user?._id ? (
        <div className="flex min-h-96 items-center justify-center rounded-lg border-2 border-dashed">
          <p className="text-sm text-gray-400">Sign in to manage documents</p>
        </div>
      ) : (
        <>
          {content?.is_upload_enabled && (
            <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm">
              <div className="flex border-b border-gray-100 bg-gray-50/60">
                {uploadTabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabChange(tab.key)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${uploadType === tab.key ? "border-b-2 border-highlightColor text-highlightColor" : "text-gray-500 hover:text-gray-700"}`}>
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="px-2 py-4">
                {uploadType === "new_folder" && (
                  <div className="mb-4">
                    <InputGroup
                      inline
                      name="name"
                      label="Folder Name"
                      inputValue={uploadFormData.name}
                      onChange={(e) => setUploadFormData((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                )}

                {isUploading ? (
                  <StorageUploadProgress
                    documentStatusList={documentStatusList}
                    onClose={() => {
                      setIsUploading(false);
                      setUploadFormData({ name: "", documents: [] });
                    }}
                  />
                ) : (
                  <StorageUploader
                    formData={uploadFormData}
                    setFormData={setUploadFormData}
                    uploadType={uploadType === "folder" ? "folder" : "file"}
                  />
                )}

                <div className="mt-4 flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    onClick={() => {
                      setUploadFormData({ name: "", documents: [] });
                      setDocumentStatusList([]);
                      setIsUploading(false);
                    }}
                    disabled={isUploading}
                    className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                    Clear
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={isUploading}
                    className="flex items-center gap-2 rounded-lg bg-highlightColor px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50">
                    {isUploading ? (
                      <>
                        <svg
                          className="h-4 w-4 animate-spin"
                          viewBox="0 0 24 24"
                          fill="none">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8z"
                          />
                        </svg>
                        Uploading…
                      </>
                    ) : uploadType === "new_folder" ? (
                      "Create Folder"
                    ) : (
                      "Upload"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mb-4">
            <Search
              placeholder="Search documents..."
              iconClasses="!h-11 !w-11"
              inputClasses="!h-11 w-full sm:max-w-xs"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>

          <div className="scrollbar-hide flex items-center overflow-x-auto whitespace-nowrap px-3 pb-3">
            <Breadcrumb
              items={pathStack.map((item, index) => ({
                label: item.label,
                icon: item.icon,
                onClick: () => handleBreadcrumbClick(item.id, index),
              }))}
            />
          </div>

          {selectedIds.length > 0 && (
            <div className="animate-fade-in bg-highlightColor/10 border-highlightColor/20 mb-3 flex items-center justify-between rounded-lg border px-4 py-2">
              <span className="text-sm font-medium text-highlightColor">{selectedIds.length} items selected</span>
              <button
                onClick={() => setDeleteModal({ isOpen: true, docId: null, isBulk: true })}
                className="flex items-center gap-2 text-sm font-semibold text-red-600 hover:text-red-700">
                <TrashIcon className="h-4 w-4" />
                Delete
              </button>
            </div>
          )}

          <div className="flex min-h-[300px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            {loadingDocuments ? (
              <div className="flex flex-grow items-center justify-center p-12">
                <Preloader
                  text="Loading documents..."
                  size="sm"
                  circleDimension="10"
                />
              </div>
            ) : (
              <>
                <div className="flex items-center border-b border-gray-100 bg-gray-50/50 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <div className="flex flex-1 items-center gap-4 p-1">
                    {content?.is_upload_enabled && (
                      <Checkbox
                        isChecked={documents.length > 0 && selectedIds.length === documents.length}
                        onChange={handleSelectAll}
                      />
                    )}
                    <span>Name</span>
                  </div>
                  <div className="flex gap-x-10">
                    <div className="hidden md:block">Date Created</div>
                    <div className="w-16">Actions</div>
                  </div>
                </div>

                <ul className="divide-y divide-gray-100">
                  {documents.length > 0 ? (
                    documents.map((doc) => {
                      const isFolder = doc.type === "folder";
                      const isChecked = selectedIds.includes(doc._id);

                      return (
                        <li
                          key={doc._id}
                          onClick={() => (isFolder ? handleFolderClick(doc) : handleOpenById(doc?._id))}
                          className={`flex cursor-pointer items-center justify-between p-3 transition hover:bg-gray-50 md:p-4 ${isFolder ? "group" : ""}`}>
                          <div className="flex min-w-0 flex-1 items-center space-x-3 p-1 md:space-x-4">
                            {content?.is_upload_enabled && (
                              <div
                                onClick={(e) => e.stopPropagation()}
                                className="shrink-0">
                                <Checkbox
                                  isChecked={isChecked}
                                  onChange={() => handleSelectOne(doc._id)}
                                />
                              </div>
                            )}

                            <div className={`shrink-0 rounded-lg p-2 ${isFolder ? "bg-amber-50" : "border border-gray-100 bg-gray-50"}`}>{isFolder ? <FolderIcon className="h-5 w-5 text-amber-500 md:h-6 md:w-6" /> : getFileIcon(doc.file?.extension)}</div>

                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-gray-900 group-hover:text-highlightColor">{doc.name}</p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span>{isFolder ? "Folder" : doc.file?.extension?.toUpperCase() || "FILE"}</span>
                                <span className="md:hidden">• {formatDate(doc?.created_at)}</span>
                                {!isFolder && <span className="hidden md:inline">• {formatFileSize(doc.file?.size_in_bytes)}</span>}
                              </div>
                            </div>
                          </div>

                          <div className="ml-2 flex shrink-0 items-center gap-x-2 md:gap-x-4">
                            <div className="hidden items-center gap-x-4 text-xs text-gray-400 md:flex">
                              {isFolder ? <ChevronRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-1" /> : <span>{formatDate(doc?.created_at)}</span>}
                              {content?.is_upload_enabled && <span className="text-gray-300">|</span>}
                            </div>

                            {content?.is_upload_enabled && (
                              <div className="flex items-center gap-x-1 md:gap-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setNewName(doc.name);
                                    setRenameModal({ isOpen: true, doc });
                                  }}
                                  className="rounded p-1.5 text-gray-500 hover:bg-gray-200">
                                  <PencilSquareIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteModal({ isOpen: true, docId: doc._id, isBulk: false });
                                  }}
                                  className="rounded p-1.5 text-red-500 hover:bg-red-100">
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })
                  ) : (
                    <li className="p-20 text-center">
                      <div className="flex flex-col items-center">
                        <div className="mb-3 rounded-full bg-gray-50 p-4">
                          <FolderIcon className="h-10 w-10 text-gray-200" />
                        </div>
                        <p className="text-sm font-medium text-gray-500">No documents found here</p>
                        <p className="text-xs text-gray-400">Upload files to see them in this folder</p>
                      </div>
                    </li>
                  )}
                </ul>
              </>
            )}
          </div>

          <PaginationFooter
            itemName="Documents"
            limit={limit}
            offset={offset}
            count={meta?.count}
            onChange={handlePaginationChange}
          />
        </>
      )}

      <Modal
        title="Rename Item"
        size="xs"
        overlayClasses="!z-[80]"
        isOpen={renameModal.isOpen}
        isLoading={isProcessing}
        onCancel={() => setRenameModal({ isOpen: false, doc: null })}
        onSuccess={confirmRename}
        defaultOptions={{ onSuccessButtonText: "Rename" }}>
        <div className="py-4">
          <InputGroup
            inline
            name="name"
            label="Name"
            inputValue={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </div>
      </Modal>

      <Modal
        title={deleteModal.isBulk ? "Delete Multiple Items" : "Delete Item"}
        size="xs"
        isOpen={deleteModal.isOpen}
        isLoading={isProcessing}
        onCancel={() => setDeleteModal({ isOpen: false, docId: null, isBulk: false })}
        onSuccess={confirmDelete}
        defaultOptions={{ onSuccessButtonText: "Delete", customStyles: { color: "red" } }}>
        <div className="py-4">
          <p className="text-sm text-gray-600">{deleteModal.isBulk ? `Are you sure you want to delete ${selectedIds.length} items?` : "Are you sure you want to delete this item?"} This action cannot be undone.</p>
        </div>
      </Modal>

      <DocumentPreviewModal
        docs={documents?.filter((item) => item.type === "file")}
        initialIndex={selectedDocIndex}
        isOpen={selectedDocIndex !== -1}
        onClose={() => setSelectedDocIndex(-1)}
      />
    </div>
  );
};

export default UploadedDocumentList;
